import { Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import { useTheme } from '../../hooks/useTheme';
import { FlightTrack } from '../../types/flightTrack';
import { formatDistance, formatDuration, formatTimestamp } from '../../utils/formatters';

interface RouteLayerProps {
  track: FlightTrack;
}

/**
 * The path a drone has flown, drawn on the fleet map (#581).
 *
 * Only the flown route — there is no planned one. This component used to draw a
 * `plannedRoute` as a dashed green line, from a field the API has never sent
 * and nothing has ever filled; it was invented along with the type it lived on.
 * A second polyline that is always empty is not a feature waiting for data, it
 * is a claim nobody checked (§8), so it is gone.
 *
 * Start and end markers are derived from the line's own ends rather than from
 * separate `startPoint` / `endPoint` fields, for the same reason: the server
 * describes the flight once, as a `LineString`, and two sources for where a
 * flight began are two things that can disagree.
 *
 * Colours match the orange return-path convention in `ShortestPathLayer` only
 * in that both are distinguishable from it: a flown track is blue and solid,
 * the path home is orange and dashed, so a pilot never reads history as
 * intention.
 */
export const RouteLayer: React.FC<RouteLayerProps> = ({ track }) => {
  const { isDarkMode } = useTheme();

  const traveledColor = isDarkMode ? '#42a5f5' : '#1976d2';
  const startColor = isDarkMode ? '#66bb6a' : '#4caf50';
  const endColor = isDarkMode ? '#ef5350' : '#f44336';

  // toFlightTrack refuses to build a track with fewer than two points, so both
  // ends exist. Read from the line rather than trusted from elsewhere.
  const start = track.traveledRoute[0];
  const end = track.traveledRoute[track.traveledRoute.length - 1];

  // The flight's summary, as the server measured it. Shown on both ends so it
  // is reachable wherever the pilot's pointer already is.
  const summary = (
    <div>
      <strong>Flown track</strong>
      <br />
      {formatDistance(track.distanceM / 1000)} &bull;{' '}
      {formatDuration(track.durationS / 60)}
      <br />
      Max altitude {track.maxAltitude.toFixed(1)} m
      <br />
      {formatTimestamp(track.startedAt)} → {formatTimestamp(track.endedAt)}
    </div>
  );

  return (
    <>
      <Polyline
        positions={track.traveledRoute}
        color={traveledColor}
        weight={4}
        opacity={0.8}
      >
        <Tooltip sticky>{summary}</Tooltip>
      </Polyline>

      {/* Where it took off. */}
      <CircleMarker
        center={start}
        radius={8}
        fillColor={startColor}
        fillOpacity={0.9}
        color="#ffffff"
        weight={2}
      >
        <Tooltip direction="top" offset={[0, -8]}>
          <div>
            <strong>Flight start</strong>
            <br />
            {formatTimestamp(track.startedAt)}
          </div>
        </Tooltip>
      </CircleMarker>

      {/* Its last reported fix in the window. */}
      <CircleMarker
        center={end}
        radius={8}
        fillColor={endColor}
        fillOpacity={0.9}
        color="#ffffff"
        weight={2}
      >
        <Tooltip direction="top" offset={[0, -8]}>
          {summary}
        </Tooltip>
      </CircleMarker>
    </>
  );
};
